import React, { useMemo, useState, useEffect } from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { useNavigate, useSearchParams, Link as RouterLink, useLocation } from 'react-router-dom';
import RaiseQueryForm from '../components/queries/RaiseQueryForm';
import { colors, spacing, fonts } from '../constants';
import { normalizeRole } from '../utils/assignmentRules';

// Region label to code mapping
const REGION_MAPPING = {
  'All': 'ALL',
  'all': 'ALL',
  'All Regions': 'ALL',
  'all regions': 'ALL',
  'North Region': 'NR',
  'north region': 'NR',
  'Northern Region': 'NR',
  'northern region': 'NR',
  'South Region': 'SR',
  'south region': 'SR',
  'Southern Region': 'SR',
  'southern region': 'SR',
  'East Region': 'ER',
  'east region': 'ER',
  'Eastern Region': 'ER',
  'eastern region': 'ER',
  'West Region': 'WR',
  'west region': 'WR',
  'Western Region': 'WR',
  'western region': 'WR',
  'North East': 'NER',
  'north east': 'NER',
  'North Eastern Region': 'NER',
  'north eastern region': 'NER',
  // 'Head Office': 'HO',
  // 'head office': 'HO',
  // 'HO Region': 'HO',
  // 'ho region': 'HO',
  // 'HO': 'HO',
  'NER': 'NER',
  'NR': 'NR',
  'SR': 'SR',
  'ER': 'ER',
  'WR': 'WR',
  'ALL': 'ALL',
};

const mapRegionToCode = (regionLabel) => {
  if (!regionLabel) return '';
  const trimmed = regionLabel.trim();
  return REGION_MAPPING[trimmed] || trimmed;
};

const RaiseQueryCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (error) {
      // console.error('Failed to parse user from localStorage', error);
      return null;
    }
  });

  // Extract and process query parameters
  const visualDescriptionParam = searchParams.get('visualDescription') || '';
  const regionParam = searchParams.get('region') || '';
  const projectParam = searchParams.get('project') || '';
  const projectNameParam = searchParams.get('projectName') || '';
  const queryTypeParam = searchParams.get('queryType') || '';
  const priorityParam = searchParams.get('priority') || '';
  const assigneeIdParam = searchParams.get('assigneeId') || '';

  // Map region label to code
  const regionCodeFromUrl = useMemo(() => {
    return mapRegionToCode(regionParam);
  }, [regionParam]);

  // Validate region based on user role
  const validatedRegion = useMemo(() => {
    if (!currentUser) return regionCodeFromUrl;
    
    const userRole = normalizeRole(currentUser.role);
    const userRegion = currentUser.region || '';
    
    // CH and MP can select any region from URL
    if (userRole === 'CH' || userRole === 'MP') {
      return regionCodeFromUrl;
    }
    
    // ED, GM can only use their own region
    if (userRole === 'ED' || userRole === 'GM') {
      // If URL has a region that matches user's region, use it
      if (regionCodeFromUrl && regionCodeFromUrl === userRegion) {
        return regionCodeFromUrl;
      }
      // Otherwise, use user's own region (ignore URL region)
      return userRegion;
    }
    
    // For other roles, use region from URL if provided
    return regionCodeFromUrl;
  }, [regionCodeFromUrl, currentUser]);

  // Use project or projectName (project takes precedence)
  const projectName = useMemo(() => {
    return projectParam || projectNameParam;
  }, [projectParam, projectNameParam]);

  // Decode URL-encoded values (handle double-encoding if present)
  const visualDescription = useMemo(() => {
    if (!visualDescriptionParam) {
      return '';
    }
    
    try {
      // URLSearchParams.get() automatically decodes once, but if the URL had double-encoding
      // (e.g., Project%2520Analysis → Project%20Analysis after first decode), we need to decode again
      let decoded = visualDescriptionParam;
      
      // Keep decoding until no more encoded characters remain (max 5 iterations for safety)
      let previousDecoded = '';
      let decodeCount = 0;
      while (decoded.includes('%') && decoded !== previousDecoded && decodeCount < 5) {
        previousDecoded = decoded;
        try {
          decoded = decodeURIComponent(decoded);
          decodeCount++;
        } catch (e) {
          // If decode fails, break and use the last successful decode
          break;
        }
      }
      
      return decoded;
    } catch (error) {
      // console.error('Error decoding visualDescription:', error);
      return visualDescriptionParam;
    }
  }, [visualDescriptionParam]);

  // Available regions for random selection
  const availableRegions = ['NR', 'SR', 'ER', 'WR', 'NER']; // 'HO' removed

  // Initialize URL with default region for CH/MP roles if no URL params exist
  // Visual description is only taken from URL, no hardcoded fallback
  useEffect(() => {
    if (!currentUser) return;
    
    const userRole = normalizeRole(currentUser.role);
    const userRegion = currentUser.region || '';
    
    // For CH and MP roles, auto-select random region if not in URL
    if ((userRole === 'CH' || userRole === 'MP') && !regionParam) {
      const newParams = new URLSearchParams(searchParams);
      
      // Select a random region (excluding ALL)
      const randomRegion = availableRegions[Math.floor(Math.random() * availableRegions.length)];
      newParams.set('region', randomRegion);
      
      setSearchParams(newParams, { replace: true });
    }
  }, [currentUser, regionParam, searchParams, setSearchParams]);

  const handleCancel = () => {
    navigate('/dashboard/queries/raise');
  };

  const handleSuccess = () => {
    navigate('/dashboard/queries/raise');
  };

  const handleFormDataChange = (formData) => {
    if (!currentUser) return;
    
    // Update URL for all roles when form data changes
    const newParams = new URLSearchParams(searchParams);
    
    if (formData.region) {
      newParams.set('region', formData.region);
    } else {
      newParams.delete('region');
    }
    
    if (formData.projectName) {
      newParams.set('projectName', formData.projectName);
    } else {
      newParams.delete('projectName');
    }
    
    if (formData.visualDescription) {
      newParams.set('visualDescription', encodeURIComponent(formData.visualDescription));
    } else {
      newParams.delete('visualDescription');
    }
    
    setSearchParams(newParams, { replace: true });
  };

  const handleProjectAutoSelected = (projectName) => {
    // This is now handled by handleFormDataChange
    handleFormDataChange({ region: validatedRegion, projectName, visualDescription });
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: colors.background,
        p: spacing.md,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md,
      }}
    >
      {/* <Box>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: fonts.fontSizeSm }}>
          <Link component={RouterLink} underline="hover" color="inherit" to="/dashboard/analytics/sla">
            SLA Dashboard
          </Link>
          <Typography color="text.primary" sx={{ fontSize: fonts.fontSizeSm }}>
            New Query
          </Typography>
        </Breadcrumbs>
      </Box> */}

      <RaiseQueryForm
        layout="page"
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        heading="Raise New Query"
        visualDescription={visualDescription}
        initialRegion={validatedRegion}
        initialProjectName={projectName}
        initialQueryType={queryTypeParam}
        initialPriority={priorityParam}
        initialAssigneeId={assigneeIdParam}
        hasUrlParams={!!(regionParam || projectNameParam || visualDescriptionParam || (currentUser && (normalizeRole(currentUser.role) === 'CH' || normalizeRole(currentUser.role) === 'MP')))}
        onProjectAutoSelected={handleProjectAutoSelected}
        onFormDataChange={handleFormDataChange}
      />
    </Box>
  );
};

export default RaiseQueryCreate;
